import { documentationClient } from 'shared/domain/documentation/documentationClient';
import type { DocumentationModel, VersionModel } from 'shared/domain/documentation/documentationModel';
import { LevelMapResolution, MapReference, NO_LEVEL_MAP, resolveMap } from 'shared/domain/documentation/levelMap';
import type { LocalizedOnMap } from 'shared/domain/issue/issueLocation';
import { ImageRotation } from 'shared/dtos/out/document';
import { createSingleFlightGroup, ReadFreshness } from 'shared/utils/singleFlight';

/** A map, ready to draw: its image and rotation; no `src` when nothing can be drawn. */
export interface LevelMapSource {
  src: string | undefined;
  imageRotation: ImageRotation;
  resolution: LevelMapResolution<VersionModel>;
}

/** What tells one map from another: the documentation and its version, or `current` while it follows the current one. */
export function getMapCacheKey(map: MapReference): string;
export function getMapCacheKey(map: MapReference | undefined): string | undefined;
export function getMapCacheKey(map: MapReference | undefined): string | undefined {
  return map ? `${map.documentationId}:${map.versionId ?? 'current'}` : undefined;
}

const documentationReads = createSingleFlightGroup<DocumentationModel>();

/**
 * One read of a documentation at a time, shared by every map and title that asks for it
 * meanwhile; after a change (`ReadFreshness.startedAfterChange`) it is read again.
 */
export function fetchDocumentationDeduped(
  documentationId: string,
  freshness: ReadFreshness = ReadFreshness.sharedWithRunning
): Promise<DocumentationModel> {
  return documentationReads.run(documentationId, () => documentationClient.getOneById(documentationId), freshness);
}

/** What a map shows, as the backend draws it, with its documentation as stored on the device. */
export async function resolveStoredMap(
  map: MapReference | undefined,
  freshness: ReadFreshness = ReadFreshness.sharedWithRunning
): Promise<LevelMapResolution<VersionModel>> {
  if (!map) {
    return NO_LEVEL_MAP;
  }
  const documentation = await fetchDocumentationDeduped(map.documentationId, freshness).catch(() => undefined);
  return resolveMap(map, () => documentation);
}

/** The version a map shows, which a location placed on it is saved with; `undefined` when none is shown. */
export async function findShownMapVersion(map: MapReference | undefined): Promise<LocalizedOnMap | undefined> {
  const { shown } = await resolveStoredMap(map);
  return shown && map ? { documentationId: map.documentationId, versionId: shown.version._id } : undefined;
}

const mapSourceReads = createSingleFlightGroup<LevelMapSource>();

/**
 * A map, ready to draw; one load at a time per map, shared by the map views that open it
 * meanwhile. After a change of its documentation it is loaded again.
 */
export function getMapSource(
  map: MapReference | undefined,
  freshness: ReadFreshness = ReadFreshness.sharedWithRunning
): Promise<LevelMapSource> {
  const mapKey = getMapCacheKey(map) ?? '';
  return mapSourceReads.run(mapKey, () => loadMapSource(map, freshness), freshness);
}

async function loadMapSource(map: MapReference | undefined, freshness: ReadFreshness): Promise<LevelMapSource> {
  const resolution = await resolveStoredMap(map, freshness);
  const { shown } = resolution;
  if (!shown || !map) {
    return { src: undefined, imageRotation: ImageRotation.ROTATE_0, resolution };
  }
  const src = await fetchMapImage(map.documentationId, shown.version._id);
  return { src, imageRotation: shown.version.imageRotation, resolution };
}

/** The map image of the documentation version (the file itself when it has no image); missing when signing failed. */
async function fetchMapImage(documentationId: string, versionId: string): Promise<string | undefined> {
  try {
    const signedUrls = await documentationClient.getSignedUrl({ documentationId, versionId });
    return signedUrls.imageSignedRequest || signedUrls.signedRequest;
  } catch {
    return undefined;
  }
}
